import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Shield, Search, ExternalLink, AlertCircle, Loader2, Building2, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SynergyPartnerAccess() {
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleValidatePin = async (accessType) => {
    if (!userEmail.trim()) {
      setError('Please enter user email');
      return;
    }
    if (!pin.trim() || pin.length !== 6) {
      setError('Please enter a valid 6-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Validate PIN with type restriction
      const response = await axios.post(`${API}/support-pin/validate`, {
        user_email: userEmail,
        pin: pin,
        reason: reason || undefined,
        access_type: accessType // 'partner' or 'client'
      });

      const { access_token, dashboard_path, target_user } = response.data;

      toast.success(`Access granted to ${target_user.email}'s dashboard`);

      // Open dashboard in new tab
      const autoLoginUrl = `${window.location.origin}/auto-login?token=${access_token}&redirect=${dashboard_path}`;
      window.open(autoLoginUrl, '_blank');

      // Reset form
      resetForm();
      setPartnerOpen(false);
      setClientOpen(false);

    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to validate PIN';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
    setError('');
  };

  const resetForm = () => {
    setUserEmail('');
    setPin('');
    setReason('');
    setError('');
  };

  const handleOpenDialog = (type) => {
    resetForm();
    if (type === 'partner') {
      setPartnerOpen(true);
    } else {
      setClientOpen(true);
    }
  };

  return (
    <>
      {/* Two separate access buttons for Synergy Partner */}
      <Button
        variant="outline"
        className="gap-2 border-green-200 text-green-700 hover:bg-green-600 hover:border-green-600"
        onClick={() => handleOpenDialog('partner')}
        data-testid="partner-dashboard-access-btn"
      >
        <Building2 className="w-4 h-4" />
        Partner Dashboard Access
      </Button>

      <Button
        variant="outline"
        className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-600 hover:border-purple-600"
        onClick={() => handleOpenDialog('client')}
        data-testid="client-dashboard-access-btn"
      >
        <Users className="w-4 h-4" />
        Client Dashboard Access
      </Button>

      {/* Partner Dashboard Access Dialog */}
      <Dialog open={partnerOpen} onOpenChange={(open) => { if (!open) resetForm(); setPartnerOpen(open); }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              Partner Dashboard Access
            </DialogTitle>
            <DialogDescription>
              Enter the Partner's email and 6-digit Support PIN to access their dashboard.
              You can only access Partners that belong to you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="partner-email">Partner Email</Label>
              <Input
                id="partner-email"
                type="email"
                placeholder="partner@example.com"
                value={userEmail}
                onChange={(e) => { setUserEmail(e.target.value); setError(''); }}
                data-testid="partner-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partner-pin">6-Digit Support PIN</Label>
              <Input
                id="partner-pin"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={pin}
                onChange={handlePinChange}
                maxLength={6}
                className="text-center text-2xl tracking-[0.3em] font-mono"
                data-testid="partner-pin-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="partner-reason">Reason for Access (Optional)</Label>
              <Textarea
                id="partner-reason"
                placeholder="Briefly describe why you need access..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="resize-none h-20"
                data-testid="partner-reason-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPartnerOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleValidatePin('partner')}
              disabled={loading || pin.length !== 6}
              className="bg-green-600 hover:bg-green-700"
              data-testid="partner-access-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Partner Dashboard
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Dashboard Access Dialog */}
      <Dialog open={clientOpen} onOpenChange={(open) => { if (!open) resetForm(); setClientOpen(open); }}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Client Dashboard Access
            </DialogTitle>
            <DialogDescription>
              Enter the Client's email and 6-digit Support PIN to access their dashboard.
              You can only access Clients that belong to you or your Partners.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client-email">Client Email</Label>
              <Input
                id="client-email"
                type="email"
                placeholder="client@example.com"
                value={userEmail}
                onChange={(e) => { setUserEmail(e.target.value); setError(''); }}
                data-testid="client-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-pin">6-Digit Support PIN</Label>
              <Input
                id="client-pin"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={pin}
                onChange={handlePinChange}
                maxLength={6}
                className="text-center text-2xl tracking-[0.3em] font-mono"
                data-testid="client-pin-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client-reason">Reason for Access (Optional)</Label>
              <Textarea
                id="client-reason"
                placeholder="Briefly describe why you need access..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="resize-none h-20"
                data-testid="client-reason-input"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setClientOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => handleValidatePin('client')}
              disabled={loading || pin.length !== 6}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="client-access-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Client Dashboard
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
